// src/components/SocialMediaMenu/SocialMediaMenu.tsx
import React, { ComponentType, SVGProps } from 'react';
import styled from 'styled-components';
import { MdOutlineFacebook } from 'react-icons/md';
import { PiPhoneCallFill } from 'react-icons/pi';
import { RiWhatsappFill } from 'react-icons/ri';
import { PiInstagramLogoFill } from 'react-icons/pi';

import { Icon } from '../StyledComponents';
import { BUSINESS_BASE } from '@/constants/core';
import { useI18n } from '@/i18n/LanguageProvider';
import { MEDIA_BREAKPOINTS } from '@/constants/layout';
import { trackEvent } from '@/utils/analytics';

interface SocialMediaMenuProps {
  orientation: 'horizontal' | 'vertical';
  showLabels?: boolean;
  compactDesktop?: boolean;
  labelTone?: 'default' | 'planning';
}

const VisuallyHidden = styled.span`
  position: absolute !important;
  height: 1px;
  width: 1px;
  overflow: hidden;
  clip: rect(1px, 1px, 1px);
  white-space: nowrap;
`;

const SocialNav = styled.nav``;

const SocialList = styled.ul<{
  $orientation: SocialMediaMenuProps['orientation'];
  $showLabels?: boolean;
  $compactDesktop?: boolean;
}>`
  list-style: none;
  padding: 0;
  margin: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  ${({ $orientation, $showLabels }) =>
    $orientation === 'horizontal'
      ? `
      flex-direction: row;
      ${$showLabels ? 'gap: clamp(12px, 5vw, 28px);' : '> li { margin: 0 10px; }'}
    `
      : `
      flex-direction: column;
      > li { margin: 10px 0; }
      padding-bottom: 10px;
    `}

  ${({ $orientation, $showLabels, $compactDesktop }) =>
    $orientation === 'horizontal' && !$showLabels && $compactDesktop
      ? `
      @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) and (max-width: 1120px) {
        > li { margin: 0 4px; }
      }
    `
      : ''}
`;

const SocialItem = styled.li``;

const SocialIconLink = styled.a<{ $showLabels?: boolean }>`
  text-decoration: none;
  color: inherit;
  position: relative;
  display: inline-block;

  ${({ $showLabels }) =>
    $showLabels
      ? `
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
      gap: 6px;
      min-width: 54px;
      min-height: 44px;
    `
      : ''}

  @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    transition:
      transform 0.25s ease,
      box-shadow 0.25s ease;

    &:hover {
      transform: scale(1.15);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
      border-radius: 50%;
    }
  }
`;

const SocialIconWrap = styled(Icon)<{
  $showLabels?: boolean;
  $compactDesktop?: boolean;
}>`
  @media (max-width: ${MEDIA_BREAKPOINTS.mediumScreen}) {
    & > svg {
      width: ${({ theme, $showLabels }) =>
        $showLabels
          ? `calc(${theme.sizes.small.iconHeight} + 3px)`
          : theme.sizes.small.iconHeight};
      height: ${({ theme, $showLabels }) =>
        $showLabels
          ? `calc(${theme.sizes.small.iconHeight} + 3px)`
          : theme.sizes.small.iconHeight};
    }
  }

  ${({ $compactDesktop }) =>
    $compactDesktop
      ? `
      @media (min-width: ${MEDIA_BREAKPOINTS.mediumScreen}) and (max-width: 1120px) {
        & > svg {
          width: 22px;
          height: 22px;
          margin-left: 2px;
        }
      }
    `
      : ''}
`;

const SocialLabel = styled.span<{
  $labelTone: NonNullable<SocialMediaMenuProps['labelTone']>;
}>`
  font-family: ${({ theme }) => theme.fonts.rubikExtraBold};
  font-size: 0.72rem;
  line-height: 1.1;
  color: ${({ theme, $labelTone }) =>
    $labelTone === 'planning' ? '#f6f0ff' : theme.descriptionColor};
  text-align: center;
  white-space: nowrap;
`;

const PhoneIconWrap = styled(SocialIconWrap)`
  & > svg {
    color: ${({ theme }) => theme.socialPhoneIconColor ?? theme.iconColor};
    background: ${({ theme }) =>
      theme.socialPhoneIconBackground ??
      `linear-gradient(135deg, ${theme.gradientColors[0]} 0%, ${theme.gradientColors[1]} 100%)`};
    border: 1px solid
      ${({ theme }) => theme.socialPhoneIconBorderColor ?? theme.borderColor};
  }
`;

const PhoneIcon = styled(
  PiPhoneCallFill as ComponentType<SVGProps<SVGSVGElement>>,
).attrs({ 'aria-hidden': true, focusable: 'false' })``;

const WhatsappIcon = styled(
  RiWhatsappFill as ComponentType<SVGProps<SVGSVGElement>>,
).attrs({ 'aria-hidden': true, focusable: 'false' })``;

const InstagramIcon = styled(
  PiInstagramLogoFill as ComponentType<SVGProps<SVGSVGElement>>,
).attrs({ 'aria-hidden': true, focusable: 'false' })``;

const FacebookIcon = styled(
  MdOutlineFacebook as ComponentType<SVGProps<SVGSVGElement>>,
).attrs({ 'aria-hidden': true, focusable: 'false' })``;

const getInstagramUrl = () =>
  BUSINESS_BASE.sameAs.find(u => u.includes('instagram.com')) ||
  'https://www.instagram.com/dj_elior_mor/';

const getFacebookUrl = () =>
  BUSINESS_BASE.sameAs.find(u => u.includes('facebook.com')) ||
  'https://www.facebook.com/DjEliorMor';

const trackPhoneClick = (href: string) => {
  trackEvent('lead_phone', {
    source: 'social',
    channel: 'phone',
    location: 'social_media_menu',
    href,
  });
};

const trackWhatsappClick = (href: string) => {
  trackEvent('lead_whatsapp', {
    source: 'social',
    channel: 'whatsapp',
    location: 'social_media_menu',
    href,
  });

  // ✅ Meta — אם אתה רוצה שוואטסאפ יחשב Lead/Contact בפיקסל
  // trackMetaLead({
  //   source: 'social',
  //   channel: 'whatsapp',
  //   location: 'social_media_menu',
  //   href,
  // });
};

const SocialMediaMenu: React.FC<SocialMediaMenuProps> = ({
  orientation,
  showLabels = false,
  compactDesktop = false,
  labelTone = 'default',
}) => {
  const { i18n } = useI18n();
  const { BRAND_TITLE_SHORT, SECTION_FOUR_CONTACT_BUTTONS } = i18n;

  const phoneHref = SECTION_FOUR_CONTACT_BUTTONS.phone.href;
  const whatsappHref = SECTION_FOUR_CONTACT_BUTTONS.whatsapp.href;
  const instagramHref = getInstagramUrl();
  const facebookHref = getFacebookUrl();
  const labels = {
    phone: SECTION_FOUR_CONTACT_BUTTONS.phone.label,
    whatsapp: SECTION_FOUR_CONTACT_BUTTONS.whatsapp.label,
    instagram: SECTION_FOUR_CONTACT_BUTTONS.instagram.label,
    facebook: SECTION_FOUR_CONTACT_BUTTONS.facebook.label,
  };
  const socialNavLabel = `${labels.phone} / ${labels.whatsapp} / ${labels.instagram} / ${labels.facebook}`;

  return (
    <SocialNav aria-label={socialNavLabel}>
      <SocialList
        $orientation={orientation}
        $showLabels={showLabels}
        $compactDesktop={compactDesktop}
        role="list"
      >
        <SocialItem role="listitem">
          <SocialIconLink
            href={phoneHref}
            aria-label={`${labels.phone} - ${BRAND_TITLE_SHORT}`}
            title={`${labels.phone} - ${BRAND_TITLE_SHORT}`}
            onClick={() => trackPhoneClick(phoneHref)}
            $showLabels={showLabels}
          >
            <PhoneIconWrap
              aria-hidden="true"
              $showLabels={showLabels}
              $compactDesktop={compactDesktop}
            >
              <PhoneIcon />
            </PhoneIconWrap>
            {showLabels ? (
              <SocialLabel $labelTone={labelTone}>{labels.phone}</SocialLabel>
            ) : (
              <VisuallyHidden>{labels.phone}</VisuallyHidden>
            )}
          </SocialIconLink>
        </SocialItem>

        <SocialItem role="listitem">
          <SocialIconLink
            href={whatsappHref}
            target="_blank"
            rel="me noopener noreferrer"
            aria-label={`${labels.whatsapp} - ${BRAND_TITLE_SHORT}`}
            title={`${labels.whatsapp} - ${BRAND_TITLE_SHORT}`}
            itemProp="sameAs"
            onClick={() => trackWhatsappClick(whatsappHref)}
            $showLabels={showLabels}
          >
            <SocialIconWrap
              $bg="linear-gradient(100deg, #25D366 0%, #FFFFFF 100%)"
              aria-hidden="true"
              $showLabels={showLabels}
              $compactDesktop={compactDesktop}
            >
              <WhatsappIcon />
            </SocialIconWrap>
            {showLabels ? (
              <SocialLabel $labelTone={labelTone}>
                {labels.whatsapp}
              </SocialLabel>
            ) : (
              <VisuallyHidden>{labels.whatsapp}</VisuallyHidden>
            )}
          </SocialIconLink>
        </SocialItem>

        <SocialItem role="listitem">
          <SocialIconLink
            href={instagramHref}
            target="_blank"
            rel="me noopener noreferrer"
            aria-label={`${labels.instagram} - ${BRAND_TITLE_SHORT}`}
            title={`${labels.instagram} - ${BRAND_TITLE_SHORT}`}
            itemProp="sameAs"
            $showLabels={showLabels}
          >
            <SocialIconWrap
              $bg="linear-gradient(
                135deg,
                #feda75 0%,
                #d62976 35%,
                #962fbf 65%,
                #4f5bd5 100%
              )"
              aria-hidden="true"
              $showLabels={showLabels}
              $compactDesktop={compactDesktop}
            >
              <InstagramIcon />
            </SocialIconWrap>
            {showLabels ? (
              <SocialLabel $labelTone={labelTone}>
                {labels.instagram}
              </SocialLabel>
            ) : (
              <VisuallyHidden>{labels.instagram}</VisuallyHidden>
            )}
          </SocialIconLink>
        </SocialItem>

        <SocialItem role="listitem">
          <SocialIconLink
            href={facebookHref}
            target="_blank"
            rel="me noopener noreferrer"
            aria-label={`${labels.facebook} - ${BRAND_TITLE_SHORT}`}
            title={`${labels.facebook} - ${BRAND_TITLE_SHORT}`}
            itemProp="sameAs"
            $showLabels={showLabels}
          >
            <SocialIconWrap
              $bg="linear-gradient(100deg, #1877F2 0%, #FFFFFF 100%)"
              aria-hidden="true"
              $showLabels={showLabels}
              $compactDesktop={compactDesktop}
            >
              <FacebookIcon />
            </SocialIconWrap>
            {showLabels ? (
              <SocialLabel $labelTone={labelTone}>
                {labels.facebook}
              </SocialLabel>
            ) : (
              <VisuallyHidden>{labels.facebook}</VisuallyHidden>
            )}
          </SocialIconLink>
        </SocialItem>
      </SocialList>
    </SocialNav>
  );
};

export default SocialMediaMenu;
